import { useTranslation } from "next-i18next";

import { cn } from "@/common/utils/css.utils";
import { withPricePlace } from "@/common/utils/intl.utils";
import { ProductVat } from "@/features/seller-invoice/data/seller-invoice-client";
import {
  useProductVat,
  useSellerInvoiceCopy,
} from "@/features/seller-invoice/hooks/useSellerInvoice";
import {
  VatPriceLine,
  getVatPriceLine,
} from "@/features/seller-invoice/utils/seller-invoice.utils";

type PricePlace = "list" | "exact" | "money";

/** WHO-6309 D7. The line itself: "€X excl. btw" or the margin legend. */
export const VatPriceLineView = ({
  line,
  place = "exact",
  className,
}: {
  line: VatPriceLine;
  place?: PricePlace;
  className?: string;
}) => {
  const { t } = useTranslation();
  const { copy } = useSellerInvoiceCopy();

  if (!line) {
    return null;
  }

  return (
    <span
      data-testid="seller-invoice-vat-line"
      className={cn("font-body text-sm text-grey-500", className)}
    >
      {line.kind === "exclVat"
        ? copy("common:seller-invoice-price-excl-vat", {
            price: t("common:common-price", {
              price: withPricePlace(line.price, place),
            }),
          })
        : copy("common:seller-invoice-margin-not-deductible")}
    </span>
  );
};

/** PDP and search card: reads the product's VAT fields for a business viewer. */
export const ProductVatPriceLine = ({
  productId,
  place,
  className,
}: {
  productId: string | null | undefined;
  place?: PricePlace;
  className?: string;
}) => {
  const { isBusinessViewer, vat } = useProductVat(productId);

  return (
    <VatPriceLineView
      line={getVatPriceLine({
        isBusinessViewer,
        vatRegime: vat?.vatRegime,
        priceExclVat: vat?.priceExclVat,
      })}
      place={place}
      className={className}
    />
  );
};

/** Cart and checkout: one cart item's VAT fields. */
export const CartVatPriceLine = ({
  isBusinessViewer,
  vat,
}: {
  isBusinessViewer: boolean;
  vat: ProductVat | null | undefined;
}) => (
  <VatPriceLineView
    line={getVatPriceLine({
      isBusinessViewer,
      vatRegime: vat?.vatRegime,
      priceExclVat: vat?.priceExclVat,
    })}
    place="money"
  />
);
